import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { menuAPI, authAPI } from '../../services/api'
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline'
import MenuItemForm from '../../components/MenuItemForm'
import CategoryForm from '../../components/CategoryForm'
import GoogleImageSearchModal from '../../components/GoogleImageSearchModal'
import toast from 'react-hot-toast'
import { useAdminBusiness } from '../../contexts/AdminBusinessContext'
import { useTheme } from '../../contexts/ThemeContext'
import CatalogImport from '../../components/CatalogImport'
import { t } from '../../i18n'

function MenuManagementPage() {
  const [showImport, setShowImport] = useState(false)
  const location = useLocation()
  const [categories, setCategories] = useState([])
  const [menuItems, setMenuItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('categories')
  const [showItemForm, setShowItemForm] = useState(false)
  const [showCategoryForm, setShowCategoryForm] = useState(false)
  const [showImageSearch, setShowImageSearch] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [editingCategory, setEditingCategory] = useState(null)
  const { businessType, branch } = useAdminBusiness()
  const { getCurrency } = useTheme()
  // /admin/menu is the menu; /admin/products and /admin/categories the online
  // store. A store running both keeps the two lists apart.
  const catalog = location.pathname.startsWith('/admin/menu') ? 'menu'
    : ['/admin/products', '/admin/categories'].some(p => location.pathname.startsWith(p)) ? 'store'
    : (businessType === 'ecommerce' ? 'store' : 'menu')
  const isEcommerce = catalog === 'store'
  const itemLabel = isEcommerce ? 'Product' : 'Menu Item'
  const itemsLabel = isEcommerce ? 'Products' : 'Menu Items'
  useEffect(() => { setActiveTab(location.pathname === '/admin/products' ? 'items' : 'categories') }, [location.pathname])


  useEffect(() => {
    loadData()
  }, [catalog])

  const loadData = async () => {
    try {
      setLoading(true)
      const [categoriesRes, itemsRes] = await Promise.all([
        menuAPI.getCategories({ branchId: branch.id, catalog }),
        menuAPI.getMenuItems({ branchId: branch.id, catalog })
      ])
      
      setCategories(categoriesRes.data.categories)
      setMenuItems(itemsRes.data.items)
    } catch (error) {
      toast.error(t("Failed to load menu data"))
      console.error('Menu load error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleAddItem = () => {
    setEditingItem(null)
    setShowItemForm(true)
  }

  const handleEditItem = (item) => {
    setEditingItem(item)
    setShowItemForm(true)
  }

  const handleSaveItem = (savedItem) => {
    if (editingItem) {
      // Update existing item
      setMenuItems(prev => prev.map(item => 
        item.id === savedItem.id ? savedItem : item
      ))
    } else {
      // Add new item
      setMenuItems(prev => [savedItem, ...prev])
    }
    setShowItemForm(false)
    setEditingItem(null)
  }

  const handleCancelForm = () => {
    setShowItemForm(false)
    setEditingItem(null)
  }

  const handleAddCategory = () => {
    setEditingCategory(null)
    setShowCategoryForm(true)
  }

  const handleEditCategory = (category) => {
    setEditingCategory(category)
    setShowCategoryForm(true)
  }

  const handleSaveCategory = (savedCategory) => {
    if (editingCategory) {
      // Update existing category
      setCategories(prev => prev.map(category => 
        category.id === savedCategory.id ? savedCategory : category
      ))
    } else {
      // Add new category
      setCategories(prev => [savedCategory, ...prev])
    }
    setShowCategoryForm(false)
    setEditingCategory(null)
  }

  const handleCancelCategoryForm = () => {
    setShowCategoryForm(false)
    setEditingCategory(null)
  }

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm(t("Are you sure you want to delete this category? This action cannot be undone."))) {
      return
    }

    try {
      await menuAPI.deleteCategory(categoryId)
      setCategories(prev => prev.filter(category => category.id !== categoryId))
      toast.success(t("Category deleted successfully"))
    } catch (error) {
      console.error('Category deletion error:', error)
      toast.error(t(error.response?.data?.error || 'Failed to delete category'))
    }
  }

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm(t("Are you sure you want to delete this menu item? This action cannot be undone."))) {
      return
    }

    try {
      await menuAPI.deleteMenuItem(itemId)
      setMenuItems(prev => prev.filter(item => item.id !== itemId))
      toast.success(t("Menu item deleted successfully"))
    } catch (error) {
      console.error('Menu item deletion error:', error)
      toast.error(t(error.response?.data?.error || 'Failed to delete menu item'))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">{t("Loading menu data...")}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {showImport && <CatalogImport isEcommerce={isEcommerce} catalog={catalog} onClose={() => setShowImport(false)} onImported={() => { setShowImport(false); loadData() }} />}
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{isEcommerce ? t("Product Management") : t("Menu Management")}</h1>
          <p className="text-gray-600">{t("Manage categories, items, variants, and modifiers")}</p>
        </div>
        <div className="flex flex-wrap gap-2"><button className="btn-secondary" onClick={() => setShowImport(true)}>{t("Import Excel / Google Sheet")}</button><button 
          onClick={handleAddItem}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 me-2" />
          {t("Add Item")}
        </button></div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('categories')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'categories'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t("Categories (")}{categories.length})
          </button>
          <button
            onClick={() => setActiveTab('items')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'items'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t(itemsLabel)} ({menuItems.length})
          </button>
        </nav>
      </div>

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="card">
          <div className="card-header">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">{t("Categories")}</h2>
              <button 
                onClick={handleAddCategory}
                className="btn-primary btn-sm"
              >
                <PlusIcon className="h-4 w-4 me-1" />
                {t("Add Category")}
              </button>
            </div>
          </div>
          <div className="card-body">
            {categories.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">{t("No categories found")}</p>
                <button 
                  onClick={handleAddCategory}
                  className="btn-primary mt-4"
                >
                  {t("Create First Category")}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category) => (
                  <div key={category.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium text-gray-900">{category.name}</h3>
                        <p className="text-sm text-gray-500">{t("Position:")} {category.position}</p>
                      </div>
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleEditCategory(category)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteCategory(category.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Menu Items Tab */}
      {activeTab === 'items' && (
        <div className="card">
          <div className="card-header">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">{t(itemsLabel)}</h2>
              <button 
                onClick={handleAddItem}
                className="btn-primary btn-sm"
              >
                <PlusIcon className="h-4 w-4 me-1" />
                {t("Add Item")}
              </button>
            </div>
          </div>
          <div className="card-body">
            {menuItems.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">{t("No menu items found")}</p>
                <button type="button" onClick={handleAddItem} className="btn-primary mt-4">{t("Create First Item")}</button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Image")}
                      </th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Item")}
                      </th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Category")}
                      </th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Price")}
                      </th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Variants")}
                      </th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Status")}
                      </th>
                      <th className="px-6 py-3 text-start text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {menuItems.map((item) => {
console.log('Rendering item:', item);
                      return <tr key={item.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <img 
                            src={ item.image}
                            alt={item.name}
                            className="h-12 w-12 rounded-lg object-cover"
                            onError={(e) => {
                                console.error('Image failed to load:', {
                                  url: item.image,
                                  error: e
                                });
                                // Add a fallback image
                                e.target.src = 'https://via.placeholder.com/100?text=No+Image';
                              }}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                            <div className="text-sm text-gray-500">{item.sku}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.category_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {parseFloat(item.price || 0).toFixed(2)} {getCurrency()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {item.variants_count > 0 ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {item.variants_count} {t("variants")}
                            </span>
                          ) : (
                            <span className="text-gray-400">{t("No variants")}</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`badge ${item.is_available ? 'badge-success' : 'badge-danger'}`}>
                            {item.is_available ? t("Available") : t("Unavailable")}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button 
                            onClick={() => handleEditItem(item)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteItem(item.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
})}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Menu Item Form Modal */}
      {showItemForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingItem ? t("Edit {itemLabel}", { itemLabel: t(itemLabel) }) : t("Add New {itemLabel}", { itemLabel: t(itemLabel) })}
                </h2>
                <button
                  onClick={handleCancelForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">{t("Close")}</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <MenuItemForm
                item={editingItem}
                categories={categories}
                onSave={handleSaveItem}
                onCancel={handleCancelForm}
                onImageSearch={() => setShowImageSearch(true)}
                branchId={1}
              />
            </div>
          </div>
        </div>
      )}

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingCategory ? t("Edit Category") : t("Add New Category")}
                </h2>
                <button
                  onClick={handleCancelCategoryForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">{t("Close")}</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <CategoryForm
                category={editingCategory}
                onSave={handleSaveCategory}
                onCancel={handleCancelCategoryForm}
                branchId={branch?.id}
                catalog={catalog}
              />
            </div>
          </div>
        </div>
      )}

      {/* Google Image Search Modal */}
      {showImageSearch && (
        <GoogleImageSearchModal
          isOpen={showImageSearch}
          onClose={() => setShowImageSearch(false)}
          onSelectImage={(imageUrl) => {
            // This will be handled in the MenuItemForm component
            const event = new CustomEvent('imageSelected', { detail: imageUrl });
            window.dispatchEvent(event);
            setShowImageSearch(false);
          }}
          searchQuery={editingItem?.name || ''}
        />
      )}
    </div>
  )
}

export default MenuManagementPage
